import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AvailabilityNote from './AvailabilityNote';
import useTableOrder from '../../hooks/order/useTableOrder';
import useTableOrderStatus from '../../hooks/order/useTableOrderStatus';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { selectTableNotice } from '../../store/slices/OrderMethodSlice';
import { TABLE_LINK_NOTICE_PARAM, TableLinkNotice } from '../../utils/on-site';
import useTableVisitEndSeen from '../../hooks/order/useTableVisitEndSeen';

interface Props {
  className?: string;
}

/**
 * The note under the order bar for table ordering: why the cart isn't at a table (a link that couldn't
 * start it, or a table the shop dropped during the visit), or, at a table, the restaurant closed or
 * paused. "You can still order for pickup" only where pickup is offered.
 */
export default function TableAvailabilityNote({ className }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const [searchParams] = useSearchParams();
  const { isAtTable } = useTableOrder();
  const { state, opensAt } = useTableOrderStatus();
  const { enabledOrderMethods, visibleOrderMethods } = useOrderMethodsStatus();
  const tableNotice = useSelector(selectTableNotice);
  // The visit's own reason is newer than the link's: the link clears it when it applies.
  const notice = tableNotice ?? searchParams.get(TABLE_LINK_NOTICE_PARAM);
  // The guest is on the menu with the visit's end: /checkout and /payment need not send them back here.
  useTableVisitEndSeen(tableNotice === TableLinkNotice.VisitEnded);
  const offersPickup =
    enabledOrderMethods.includes(ORDER_METHODS.PICKUP) && visibleOrderMethods.includes(ORDER_METHODS.PICKUP);

  if (!isAtTable && notice === TableLinkNotice.TableUnavailable) {
    return (
      <AvailabilityNote
        variant="warning"
        title={t('notes.table_unavailable.title')}
        text={t(offersPickup ? 'notes.table_unavailable.text' : 'notes.table_unavailable.text_no_pickup')}
        className={className}
        testId="table-unavailable-note"
      />
    );
  }

  if (!isAtTable && notice === TableLinkNotice.TableOrderingOff) {
    return (
      <AvailabilityNote
        variant="info"
        title={t('notes.table_ordering_off.title')}
        text={offersPickup ? t('notes.table_ordering_off.text') : undefined}
        className={className}
        testId="table-ordering-off-note"
      />
    );
  }

  // Shown at a table too: a re-scan that failed while the cart still sits at an earlier table would
  // otherwise say nothing, and the guest may well be at the other table by now.
  if (notice === TableLinkNotice.LoadFailed) {
    return (
      <AvailabilityNote
        variant="warning"
        title={t('notes.load_failed.title')}
        text={t('notes.load_failed.text')}
        className={className}
        testId="table-load-failed-note"
      />
    );
  }

  // Only a new scan returns to the table, so say so while the restaurant still takes table orders.
  if (!isAtTable && notice === TableLinkNotice.VisitEnded && enabledOrderMethods.includes(ORDER_METHODS.ON_SITE)) {
    return (
      <AvailabilityNote
        variant="info"
        title={t('eating_here.title')}
        text={t('eating_here.scan_code')}
        className={className}
        testId="table-visit-ended-note"
      />
    );
  }

  if (!isAtTable || state === 'open') {
    return null;
  }

  return (
    <AvailabilityNote
      variant="warning"
      title={t(`notes.${state}.title`)}
      text={state === 'closed' && !opensAt ? t('notes.closed.text_later') : t(`notes.${state}.text`, { time: opensAt })}
      className={className}
      testId={`table-${state}-note`}
    />
  );
}
